import type { ManualFxQuote } from './useManualFxQuote';

export interface ConversionRecapProps {
  quote: ManualFxQuote | null;
  isQuoting: boolean;
  error: string | null;
}

/**
 * What the operator is about to book, in words, plus the instruction to
 * send the liquidity provider (#273).
 *
 * The figures come from the server, so this says what will actually
 * happen rather than a second opinion about it.
 */
export function ConversionRecap({ quote, isQuoting, error }: ConversionRecapProps) {
  if (error) {
    return (
      <p className="mt-2 text-xs text-[#c62828]" role="alert" data-testid="conversion-recap-error">
        {error}
      </p>
    );
  }

  if (!quote) {
    return (
      <p className="mt-2 text-xs text-[#999]" data-testid="conversion-recap-empty">
        {isQuoting ? 'Working it out…' : 'Fill in the amount and both rates to see the recap.'}
      </p>
    );
  }

  const sell = `${quote.sell_amount} ${quote.sell_currency_code}`;
  const buy = `${quote.buy_amount} ${quote.buy_currency_code}`;
  const margin = `${quote.margin} ${quote.margin_currency_code}`;

  // Une marge negative veut dire que la maison paie la conversion. Le
  // recapitulatif l'annoncait en vert et prefixee d'un « + », juste a cote
  // de l'avertissement rouge qui dit l'inverse.
  const marginIsNegative = quote.margin.startsWith('-');
  const marginTone = marginIsNegative ? 'text-[#c62828]' : 'text-[#2e7d32]';
  const marginSign = marginIsNegative ? '' : '+';

  return (
    <div
      className="mt-2 space-y-2 rounded-lg border border-[#f0dde4] bg-[#fdf6f8] px-4 py-3 text-[13px] text-[#333]"
      data-testid="conversion-recap"
    >
      <p>
        Client <strong>SELLS {sell}</strong> and receives <strong>{buy}</strong> (client rate).
      </p>
      <p>
        <span className="font-semibold text-[#6a1b9a]">LP →</span> send {quote.lp_cost}{' '}
        {quote.sell_currency_code} · receive {buy}.
      </p>
      <p>
        Deliver {buy} to the client ·{' '}
        <span className={`font-semibold ${marginTone}`}>
          {marginIsNegative ? 'lose' : 'keep'} {margin}
        </span>{' '}
        (netted off before sending to the LP).
      </p>
      <p className={`border-t border-[#f0dde4] pt-2 font-semibold ${marginTone}`}>
        Montelux margin: {marginSign}
        {margin}
      </p>
    </div>
  );
}
